import { Loader } from "@trackman/web-shared-components";
import { AppButton, AppInput, AppTextarea } from "components/form";
import { AppDialog, AppSectionErrorBoundary, AppSpacer } from "components/ui";
import { AppAutoComplete, AutoCompleteOptionType } from "components/ui/AppAutoComplete";
import { useStores } from "index";
import { observer } from "mobx-react-lite";
import { TrackingDiagnosticsAccordion } from "pages/TrackingDiagnostics/components/TrackingDiagnosticsAccordion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IDiagnosticsPackages } from "types/models/DiagnosticsPackages";

interface IFormDiagnosticsPackageDiaglogProps {
    isOpened: boolean;
    onClose: () => void;
    onSuccessAfterClose: () => void;
    diagnosticsPackage: IDiagnosticsPackages | null;
    selectedEntries?: string[];
}

export const FormDiagnosticsPackageDiaglog = observer(({ isOpened, onClose, onSuccessAfterClose, diagnosticsPackage, selectedEntries, }: IFormDiagnosticsPackageDiaglogProps) => {
    const { t } = useTranslation();
    const { diagnosticsPackagesStore } = useStores();

    const formMode = diagnosticsPackage?.id ? 'edit' : 'add';

    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string | null>(null);
    const [packageTag, setPackageTag] = useState<string>('');
    const [formSelectedEntries, setFormSelectedEntries] = useState<string[]>([]);

    useEffect(() => {
        setName(diagnosticsPackage?.name || '');
        setDescription(diagnosticsPackage?.description || '');
        setPackageTag(diagnosticsPackage?.packageTags[0] || '');
        setFormSelectedEntries(diagnosticsPackage?.trackingDiagnosticsEntries || selectedEntries || []);
    }, [diagnosticsPackage, selectedEntries]);

    const handleDiagnosticsPackageAddedSuccessfully = useCallback(() => {
        onClose();
        onSuccessAfterClose();
    }, []);

    const onCancelHandler = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose]);

    const resetForm = useCallback(() => {
        setName('');
        setDescription(null);
        setPackageTag('');
        setFormSelectedEntries([]);
    }, []);

    const onSubmitHandler = async () => {
        if (!setName || !setPackageTag) {
            return;
        }

        if (formMode === 'edit') {
            await diagnosticsPackagesStore.patchDiagnosticsPackage(
                name,
                description,
                packageTag,
                formSelectedEntries,
                () => {
                    resetForm();
                    onClose();
                }
            );
            return;
        }

        await diagnosticsPackagesStore.createDiagnosticsPackage(
            name,
            description,
            packageTag,
            formSelectedEntries,
            () => {
                resetForm();
                handleDiagnosticsPackageAddedSuccessfully();
            }
        );
    };

    const isSubmitButtonDisabled = () => {
        if (!name || !packageTag || formSelectedEntries.length === 0) return true;
        if (diagnosticsPackagesStore.isFormLoading) return true;
        if (
            formMode === 'edit' &&
            !diagnosticsPackagesStore.isSelectedDiagnosticsPackageChanged(
                {
                    name: name,
                    description: description,
                    packageTags: [packageTag],
                    trackingDiagnosticsEntries: formSelectedEntries
                }
            )
        ) return true;

        return false;
    };

    const OnRemoveHandler = (value: string) => {
        if (!value || formSelectedEntries.length === 0)
            return;

        const updatedEntries = formSelectedEntries.filter(entryId => entryId !== value);
        setFormSelectedEntries(updatedEntries);
    };

    type PackagetagOptingType = AutoCompleteOptionType<string>;

    const selectablePackageTags = useMemo<PackagetagOptingType[]>(
        () => diagnosticsPackagesStore.diagnosticsPackagesTags.map(x => ({ inputValue: x, title: x, data: x })
        ),
        [diagnosticsPackagesStore.diagnosticsPackagesTags]
    );

    return (
        <AppDialog
            title={formMode === 'add' ? `${t('DiagnosticsPackages.Dialog.Title.Add')}` : `${t('DiagnosticsPackages.Dialog.Title.Edit', { diagnosticsPackageName: name || '' })}`}
            isOpened={isOpened}
            onClose={onClose}
        >
            <div className='flex-auto d-flex flex-column justify-content-between' id="modal-root">
                <AppSectionErrorBoundary>
                    <div>
                        <section className='container'>
                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('DiagnosticsPackages.Section.Form')}</span>
                            </h3>
                            <AppInput
                                label={t('DiagnosticsPackages.Column.Name')}
                                name={'name'}
                                value={name}
                                disabled={diagnosticsPackagesStore.isFormLoading}
                                onChange={e => setName(e.target.value)}
                            />
                            <AppSpacer space='15px' />
                            <AppAutoComplete
                                value={packageTag}
                                onValueChange={setPackageTag}
                                isLoading={diagnosticsPackagesStore.isFormLoading}
                                allOptions={selectablePackageTags}
                                label={t('DiagnosticsPackages.Column.PackageTag')}
                            />
                            <AppSpacer space='15px' />
                            <AppTextarea
                                label={t('DiagnosticsPackages.Column.Description')}
                                name={'description'}
                                value={description ?? ''}
                                disabled={diagnosticsPackagesStore.isFormLoading}
                                onChange={e => setDescription(e.target.value)}
                            />
                            <AppSpacer space='15px' />
                            {formMode === 'edit' && (
                                <div>
                                    <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                        <span className='mr-2'>{t('DiagnosticsPackages.Section.Info')}</span>
                                    </h3>
                                    <div className='d-flex flex-row my-3'>
                                        <AppInput
                                            label={t('DiagnosticsPackages.Column.CreatorId')}
                                            name={'creatorId'}
                                            value={diagnosticsPackage?.createdByUserId ?? '-'}
                                            disabled={true}
                                        />
                                        <AppSpacer isHorizontal space='1rem' />
                                        <AppInput
                                            label={t('DiagnosticsPackages.Column.CreatorEmail')}
                                            name={'email'}
                                            value={diagnosticsPackage?.creatorEmail ?? '-'}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className='d-flex flex-row my-3'>
                                        <AppInput
                                            label={t('DiagnosticsPackages.Column.AddedAt')}
                                            name={'addedAtUtc'}
                                            value={diagnosticsPackage?.addedAtUtc ?? '-'}
                                            disabled={true}
                                        />
                                        <AppSpacer isHorizontal space='1rem' />
                                        <AppInput
                                            label={t('DiagnosticsPackages.Column.UpdatedAt')}
                                            name={'updatedAtUtc'}
                                            value={diagnosticsPackage?.updatedAtUtc ?? '-'}
                                            disabled={true}
                                        />
                                    </div>
                                    <AppSpacer space='15px' />
                                </div>
                            )}

                            <TrackingDiagnosticsAccordion entryIds={formSelectedEntries} onRemove={OnRemoveHandler} hideGotoButton={formMode === 'add'} />
                        </section>
                        <AppSpacer space='20px' />
                    </div>
                    <footer className='d-flex align-items-center justify-content-between p-3 bg-white position-sticky border-top bottom-0 left-0 w-100 z-index-9'>
                        <AppButton variant='secondary' onClick={onCancelHandler} data-test='diagnostics-package-dialog-cancel-button'>
                            {t('cancel')}
                        </AppButton>
                        <div className='d-flex'>
                            {diagnosticsPackagesStore.isFormLoading && <Loader />}
                            <AppSpacer space='10px' />
                            <AppButton
                                variant='primary'
                                disabled={isSubmitButtonDisabled()}
                                onClick={onSubmitHandler}
                                data-test='diagnostics-package-dialog-submit-button'
                            >
                                {t('submit')}
                            </AppButton>
                        </div>
                    </footer>
                </AppSectionErrorBoundary>
            </div>
        </AppDialog>
    );
});